import type { Product, PriceVariant, ProductVariant } from '../types/index.js';

type VariantLike =
  | Pick<ProductVariant, 'stock' | 'orderable' | 'supplier_backed'>
  | Pick<PriceVariant, 'stock' | 'orderable' | 'supplier_backed'>;

type ProductLike = Pick<Product, 'type' | 'stock' | 'orderable' | 'supplier_backed' | 'on_hold' | 'price_variants' | 'variants'> & {
  onHold?: boolean | number | string | null;
  is_on_hold?: boolean | number | string | null;
  isOnHold?: boolean | number | string | null;
};

/** The sentinel every predicate in this module already treats as "unbounded". */
const UNLIMITED_STOCK = -1;

/**
 * Dynamic Delivery (S8): fold `orderable` into the stock value.
 *
 * `orderable` is the backend's authoritative buyability predicate — `local stock
 * covers one unit OR an eligible supplier source can fulfil the line` — and it is
 * exactly what the order-time gates apply. A supplier-backed row holds NO local
 * stock by design, so gating the buyer on `stock` alone renders it "out of stock"
 * and the purchase can never happen.
 *
 * Rather than introduce a fourth availability state, a row that is orderable
 * without local units is normalised to the unlimited sentinel — the same value an
 * unlimited row already carries. Every downstream badge, quantity cap, variant
 * gate and CTA then behaves as it already does for unlimited stock.
 *
 * `orderable` alone cannot decide how far to widen: it is also `true` for an
 * ordinary locally-stocked row, whose finite counter must stay finite. That is what
 * `supplier_backed` settles. A supplier-backed row is fulfilled by the supplier
 * purchase and the completion excludes it from the local decrement, so any residual
 * local units bound NOTHING — its cap is unlimited even at `stock: 1`. A row that is
 * NOT supplier-backed keeps its exact finite counter, and only widens from 0 (the
 * product-wide mapping case, where the backend folded the mapping into `orderable`
 * and left `stock` at 0).
 *
 * When `orderable` is absent (an older payload or a cached response shape that
 * predates the field) the raw value is returned UNCHANGED, and an absent
 * `supplier_backed` widens nothing beyond what the previous rule already did. That
 * is the only permitted fallback direction: a missing field must never flip a
 * genuinely sold-out row into a buyable one, nor a finite counter into unlimited.
 */
function applyOrderable(stock: number, orderable: unknown, supplierBacked?: unknown): number {
  if (orderable !== true) {
    return stock;
  }
  // Supplier-backed: local units do not bound the line, at any stock value.
  if (supplierBacked === true) {
    return UNLIMITED_STOCK;
  }
  // Already buyable or already unbounded — nothing to widen.
  if (stock !== 0) {
    return stock;
  }
  return UNLIMITED_STOCK;
}

function normalizeStockValue(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null;
  }

  return Math.trunc(value);
}

function isTruthyFlag(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value === 'number' && Number.isFinite(value)) return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }
  return false;
}

function isProductOnHold(product: ProductLike | null | undefined): boolean {
  if (!product) return false;
  return isTruthyFlag(product.on_hold)
    || isTruthyFlag(product.onHold)
    || isTruthyFlag(product.is_on_hold)
    || isTruthyFlag(product.isOnHold);
}

function collectVariantStocks(product: ProductLike): number[] {
  const priceVariants = Array.isArray(product.price_variants) ? product.price_variants : [];
  if (priceVariants.length > 0) {
    return priceVariants.map((variant) => resolveVariantStockValue(variant));
  }

  const legacyVariants = Array.isArray(product.variants) ? product.variants : [];

  return legacyVariants.map((variant) => resolveVariantStockValue(variant));
}

export function resolveVariantStockValue(variant: VariantLike | null | undefined): number {
  const normalized = normalizeStockValue(variant?.stock);
  return applyOrderable(normalized ?? UNLIMITED_STOCK, variant?.orderable, variant?.supplier_backed);
}

export function resolveDisplayStock(product: ProductLike | null | undefined): number {
  if (!product) {
    return UNLIMITED_STOCK;
  }

  if (typeof product.type === 'string' && product.type.toUpperCase() === 'SERIALS') {
    return applyOrderable(
      normalizeStockValue(product.stock) ?? UNLIMITED_STOCK,
      product.orderable,
      product.supplier_backed,
    );
  }

  const variantStocks = collectVariantStocks(product);
  if (variantStocks.length > 0) {
    // A single unbounded option (including a supplier-backed one, already folded
    // into the sentinel above) makes the product unbounded — summing finite
    // siblings would understate it.
    if (variantStocks.some((stock) => stock < 0)) {
      return UNLIMITED_STOCK;
    }

    const total = variantStocks.reduce((sum, stock) => sum + Math.max(stock, 0), 0);
    // The product-level flag still applies: the backend folds a product-wide
    // supplier mapping into it, and that mapping backs options with no local units.
    return applyOrderable(total, product.orderable, product.supplier_backed);
  }

  return applyOrderable(
    normalizeStockValue(product.stock) ?? UNLIMITED_STOCK,
    product.orderable,
    product.supplier_backed,
  );
}

export function isProductOutOfStock(product: ProductLike | null | undefined): boolean {
  // `on_hold` is a merchant pause, not a supply signal — it forces unavailable
  // BEFORE any `orderable` widening can apply.
  if (isProductOnHold(product)) return true;
  return resolveDisplayStock(product) === 0;
}

export function isProductInStock(product: ProductLike | null | undefined): boolean {
  return !isProductOutOfStock(product);
}

export function isVariantOutOfStock(variant: VariantLike | null | undefined): boolean {
  return resolveVariantStockValue(variant) === 0;
}
